<template>
  <div>
    <div style="margin-bottom: 10px;">
      {{ t('Clipboard Import') }}:
    </div>
    <b-form-textarea
      v-model="clipboardText"
      :placeholder="t('Вставьте данные из Excel с помощью Ctrl+V. Первая строка данных должна содержать названия столбцов как в таблице. Paste data from Excel here (Ctrl+V)...')"
      rows="4"
      max-rows="8"
    />
    <b-button-toolbar
      key-nav
      class="demo-inline-spacing mt-1"
    >
      <b-button-group
        size="sm"
        class="cbs-inline-spacing"
      >
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          :disabled="!clipboardText || parsing"
          @click="parseClipboard"
        >
          <feather-icon icon="CheckIcon" />
          {{ parsing ? t('Loading refs...') : t('Parse') }}
        </b-button>
      </b-button-group>
      <b-button-group
        size="sm"
        class="cbs-inline-spacing"
      >
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="outline-primary"
          :disabled="!clipboardText"
          @click="clearClipboard"
        >
          <feather-icon icon="XIcon" />
          {{ t('Clear') }}
        </b-button>
      </b-button-group>
      <b-button-group
        v-if="parsedRows.length > 0"
        size="sm"
        class="cbs-inline-spacing"
      >
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="success"
          @click="doImport"
        >
          <feather-icon icon="UploadIcon" />
          {{ t('Import') }} ({{ parsedRows.length }})
        </b-button>
      </b-button-group>
      <b-button-group
        v-if="parsedRows.length > 0"
        size="sm"
        class="cbs-inline-spacing"
      >
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="success"
          @click="doImportSilent"
        >
          <feather-icon icon="UploadIcon" />
          {{ t('Import Silent') }} ({{ parsedRows.length }})
        </b-button>
      </b-button-group>
    </b-button-toolbar>
    <div
      v-if="mappingInfo"
      class="mt-1"
    >
      <small>
        <span class="text-success">{{ t('Mapped') }}: {{ mappedCount }}</span>
        <span
          v-if="unmappedHeaders.length > 0"
          class="text-warning ml-1"
        >
          | {{ t('Unmapped') }}: {{ unmappedHeaders.join(', ') }}
        </span>
      </small>
    </div>
    <b-table
      v-if="parsedRows.length > 0"
      class="mt-1"
      responsive
      small
      bordered
      :fields="previewFields"
      :items="previewRows"
    />
  </div>
</template>

<script>
import {
  BFormTextarea, BButton, BButtonGroup, BButtonToolbar, BTable,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsClipboardImport',
  components: {
    BFormTextarea,
    BButton,
    BButtonGroup,
    BButtonToolbar,
    BTable,
  },
  directives: {
    Ripple,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { t } = useI18nUtils()
    return { t }
  },
  data() {
    return {
      clipboardText: '',
      parsedRows: [],
      headers: [],
      mapping: {},
      mappingInfo: false,
      parsing: false,
      refCache: {},
    }
  },
  computed: {
    mappedCount() {
      return Object.keys(this.mapping).length
    },
    unmappedHeaders() {
      return this.headers.filter((h, i) => !Object.prototype.hasOwnProperty.call(this.mapping, i))
    },
    previewFields() {
      return Object.values(this.mapping).map(fieldKey => {
        const fld = this.fields.find(f => f.key === fieldKey)
        return {
          key: fieldKey,
          label: fld ? fld.label : fieldKey,
        }
      })
    },
    previewRows() {
      return this.parsedRows.map(row => {
        const preview = {}
        const cellVariants = {}
        Object.keys(row).forEach(key => {
          const val = row[key]
          if (val && typeof val === 'object' && val.title !== undefined) {
            preview[key] = val.title || ''
            if (val.value === null && val.title) {
              cellVariants[key] = 'danger'
            }
          } else {
            preview[key] = val
          }
        })
        if (Object.keys(cellVariants).length > 0) {
          // eslint-disable-next-line no-underscore-dangle
          preview._cellVariants = cellVariants
        }
        return preview
      })
    },
  },
  methods: {
    async parseClipboard() {
      if (!this.clipboardText) return
      const lines = this.clipboardText.replace(/\r\n/g, '\n').split('\n').filter(line => line.trim() !== '')
      if (lines.length < 2) return
      this.headers = lines[0].split('\t').map(h => h.trim())
      this.mapping = {}
      this.headers.forEach((header, index) => {
        const headerLower = header.toLowerCase()
        const matched = this.fields.find(f => f.label && f.label.toLowerCase() === headerLower)
        if (matched) {
          this.mapping[index] = matched.key
        }
      })
      this.mappingInfo = true
      // load refs for mapped ref fields
      this.parsing = true
      await this.loadRefs()
      this.parsing = false
      // build rows
      this.parsedRows = []
      for (let i = 1; i < lines.length; i += 1) {
        const cols = lines[i].split('\t')
        const row = {}
        Object.keys(this.mapping).forEach(idx => {
          const fieldKey = this.mapping[idx]
          const rawVal = cols[idx] !== undefined ? cols[idx].trim() : ''
          const fld = this.fields.find(f => f.key === fieldKey)
          if (fld && fld.datatype === 'ref' && this.refCache[fieldKey]) {
            const found = this.findRef(this.refCache[fieldKey], rawVal)
            row[fieldKey] = found || { value: null, title: rawVal }
          } else if (fld && (fld.datatype === 'int' || fld.datatype === 'double' || fld.datatype === 'float')) {
            row[fieldKey] = this.parseNumeric(rawVal, fld.datatype)
          } else {
            row[fieldKey] = rawVal
          }
        })
        this.parsedRows.push(row)
      }
    },
    async loadRefs() {
      const refFields = this.refMappedFields()
      const promises = refFields.map(async fld => {
        if (this.refCache[fld.key]) return
        const ref = await useCubus.loadRef(this, { entityid: fld.entityid })
        if (ref) {
          this.$set(this.refCache, fld.key, ref)
        }
      })
      await Promise.all(promises)
    },
    refMappedFields() {
      const mappedKeys = Object.values(this.mapping)
      return this.fields.filter(f => mappedKeys.includes(f.key) && f.datatype === 'ref')
    },
    parseNumeric(raw, datatype) {
      if (raw === '' || raw === null || raw === undefined) return null
      // remove thousand separators (space, non-breaking space, thin space)
      const cleaned = raw.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.')
      const num = Number(cleaned)
      if (Number.isNaN(num)) return null
      return datatype === 'int' ? Math.round(num) : num
    },
    findRef(refList, text) {
      if (!text) return null
      const lower = text.toLowerCase()
      const found = refList.find(r => r.title && r.title.toLowerCase() === lower)
      return found ? { value: found.value, title: found.title } : null
    },
    clearClipboard() {
      this.clipboardText = ''
      this.parsedRows = []
      this.headers = []
      this.mapping = {}
      this.mappingInfo = false
      this.refCache = {}
    },
    doImport() {
      this.$emit('import', this.parsedRows)
    },
    doImportSilent() {
      this.$emit('import-silent', this.parsedRows)
    },
  },
}
</script>
